import React, { createContext, useContext, useState, useEffect } from 'react';
import { getApiUrl } from '../config/environment';

declare global {
  interface Window {
    google: any;
  }
}

interface User {
  email: string;
  name: string;
  picture: string;
}

interface AuthContextType {
  user: User | null;
  isAdmin: boolean;
  isLoading: boolean;
  isCheckingAdmin: boolean; // 관리자 권한 확인 중 상태 추가
  isGuestMode: boolean;
  login: () => Promise<void>;
  logout: () => void;
  enterGuestMode: () => void;
  canSaveAnalysis: boolean;
  canLoadAnalysis: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isCheckingAdmin, setIsCheckingAdmin] = useState(false); // 관리자 권한 확인 중 상태
  const [isGuestMode, setIsGuestMode] = useState(false);
  const [googleClientId, setGoogleClientId] = useState<string | null>(null);
  
  // 사용자 모드별 기능 가능 여부
  const canSaveAnalysis = user !== null && !isGuestMode;
  const canLoadAnalysis = user !== null && !isGuestMode;

  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      // 개발 환경에서 스텁 인증 체크
      if (process.env.NODE_ENV === 'development') {
        console.log('🔧 개발 모드 감지 - 스텁 인증 활성화');

        // 백엔드 연결 확인
        try {
          const healthCheck = await fetch('/api/health');
          if (!healthCheck.ok) {
            throw new Error('Backend not available');
          }
        } catch (backendError) {
          console.log('⚠️ 백엔드 서버 연결 실패 - 로컬 개발 모드로 전환');

          // 로컬 개발용 가짜 사용자 자동 로그인
          const devUser: User = {
            email: 'hoza8895@gmail.com', // 실제 이메일 사용 (DB 관리자 확인용)
            name: 'Dev User',
            picture: 'https://via.placeholder.com/150/0066CC/FFFFFF?text=DEV'
          };

          setUser(devUser);
          setIsAdmin(true); // 개발 환경에서는 자동 관리자 권한

          // 개발 환경용 가짜 토큰 생성 (API 호출용)
          const devToken = 'dev-token-' + Date.now();
          localStorage.setItem('google_auth_token', devToken);
          console.log('🔧 개발용 토큰 생성:', devToken);

          setIsLoading(false);
          console.log('🔧 개발용 사용자로 자동 로그인됨 (관리자 권한):', devUser.email);
          return;
        }
      }

      // 1. Google Client ID 가져오기 (중앙화된 환경 설정 사용)
      console.log('🔄 Google Client ID 요청 중...');
      const configResponse = await fetch(getApiUrl('/api/auth/config'));
      if (!configResponse.ok) {
        throw new Error('Google Client ID 설정을 가져올 수 없습니다.');
      }

      const config = await configResponse.json();
      const clientId = config.clientId;
      setGoogleClientId(clientId);
      console.log('✅ Google Client ID 획득:', clientId?.substring(0, 20) + '...');

      // 2. Google 스크립트 로드
      await loadGoogleScript();
      console.log('✅ Google Identity Services 스크립트 로드 완료');

      // 3. 기존 토큰 확인
      await checkExistingToken();

    } catch (error) {
      console.error('❌ 인증 초기화 실패:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadGoogleScript = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (window.google?.accounts?.oauth2) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Google Script 로드 실패'));
      document.head.appendChild(script);
    });
  };

  const checkAdminStatus = async (token: string): Promise<boolean> => {
    try {
      console.log('🔍 관리자 권한 확인 중...');
      const response = await fetch(getApiUrl('/api/auth/check-admin'), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const adminStatus = data.isAdmin || false;
        console.log(adminStatus ? '✅ 관리자 권한 확인됨' : 'ℹ️ 일반 사용자');
        return adminStatus;
      } else {
        console.log('ℹ️ 관리자 확인 실패, 일반 사용자로 처리');
        return false;
      }
    } catch (error) {
      console.error('❌ 관리자 확인 중 오류:', error);
      return false;
    }
  };

  const checkExistingToken = async () => {
    const token = localStorage.getItem('google_auth_token');
    if (!token) return;

    try {
      console.log('🔍 기존 토큰 검증 중...');
      const response = await fetch(getApiUrl('/api/verify-token'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        console.log('✅ 기존 토큰으로 자동 로그인:', userData.email);

        // 관리자 권한 확인
        const adminStatus = await checkAdminStatus(token);
        setIsAdmin(adminStatus);
      } else {
        localStorage.removeItem('google_auth_token');
        console.log('ℹ️ 기존 토큰이 만료되어 제거됨');
      }
    } catch (error) {
      console.error('❌ 토큰 검증 실패:', error);
      localStorage.removeItem('google_auth_token');
    }
  };


  const login = async (): Promise<void> => {
    return new Promise((resolve, reject) => {
      // 개발 모드에서는 바로 개발 사용자로 로그인
      if (process.env.NODE_ENV === 'development') {
        console.log('🔧 개발 모드 - 구글 OAuth 우회하고 바로 로그인');
        const devUser: User = {
          email: 'hoza8895@gmail.com', // 실제 이메일 사용 (DB 관리자 확인용)
          name: 'Dev User',
          picture: 'https://via.placeholder.com/150/0066CC/FFFFFF?text=DEV'
        };
        setUser(devUser);
        setIsAdmin(true); // 개발 환경에서는 자동 관리자 권한
        setIsGuestMode(false);

        // 개발 환경용 가짜 토큰 생성 (API 호출용)
        const devToken = 'dev-token-' + Date.now();
        localStorage.setItem('google_auth_token', devToken);
        console.log('🔧 개발용 토큰 생성:', devToken);

        console.log('✅ 개발 모드 로그인 완료 (관리자 권한):', devUser.email);
        resolve();
        return;
      }

      // 프로덕션 모드에서만 실제 Google OAuth 실행
      if (!googleClientId) {
        reject(new Error('Google 로그인이 준비되지 않았습니다.'));
        return;
      }

      if (!window.google?.accounts?.oauth2) {
        reject(new Error('Google Identity Services가 로드되지 않았습니다.'));
        return;
      }

      console.log('🚀 Google OAuth2 팝업 로그인 시작');

      // OAuth2 토큰 클라이언트 생성 및 실행
      const tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: googleClientId,
        scope: 'email profile',
        callback: async (response: any) => {
          try {
            console.log('🔍 OAuth 응답 받음:', response);
            
            if (!response.access_token) {
              throw new Error('Access token을 받지 못했습니다.');
            }

            await handleGoogleAuth(response.access_token);
            resolve();
            
          } catch (error) {
            console.error('❌ OAuth 콜백 처리 실패:', error);
            reject(error);
          }
        },
        error_callback: (error: any) => {
          console.error('❌ OAuth 에러:', error);
          reject(new Error(`로그인 실패: ${error.message || '알 수 없는 오류'}`));
        }
      });

      // 팝업으로 로그인 시작
      tokenClient.requestAccessToken({
        prompt: 'select_account'
      });
    });
  };

  const handleGoogleAuth = async (accessToken: string) => {
    console.log('🔄 Google 사용자 정보 요청 중...');
    
    // Google UserInfo API로 사용자 정보 가져오기
    const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });

    if (!userInfoResponse.ok) {
      throw new Error(`사용자 정보 요청 실패: ${userInfoResponse.status}`);
    }

    const userInfo = await userInfoResponse.json();
    console.log('📋 Google 사용자 정보:', userInfo);

    if (!userInfo.email || !userInfo.name) {
      throw new Error('필수 사용자 정보가 누락되었습니다.');
    }

    // 백엔드에 인증 요청
    console.log('🔄 백엔드 JWT 토큰 요청 중...');
    const authResponse = await fetch(getApiUrl('/api/auth/oauth'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user: {
          email: userInfo.email,
          name: userInfo.name,
          picture: userInfo.picture
        },
        accessToken: accessToken
      })
    });

    if (!authResponse.ok) {
      const errorText = await authResponse.text();
      throw new Error(`백엔드 인증 실패: ${errorText}`);
    }

    const { token } = await authResponse.json();

    // JWT 토큰 저장 및 상태 업데이트
    localStorage.setItem('google_auth_token', token);
    console.log('💾 JWT 토큰 저장 완료 (길이:', token?.length, ')');

    // 저장 확인
    const savedToken = localStorage.getItem('google_auth_token');
    console.log('✅ 저장된 토큰 확인:', savedToken ? '성공' : '실패');

    setUser({
      email: userInfo.email,
      name: userInfo.name,
      picture: userInfo.picture
    });
    setIsGuestMode(false);

    // 관리자 권한 확인 시작
    console.log('🔄 관리자 권한 확인 시작...');
    setIsCheckingAdmin(true);

    const adminStatus = await checkAdminStatus(token);
    setIsAdmin(adminStatus);

    setIsCheckingAdmin(false);
    console.log('✅ 관리자 권한 확인 완료:', adminStatus);

    console.log('✅ 로그인 완료:', userInfo.email);
  };

  const logout = () => {
    localStorage.removeItem('google_auth_token');
    setUser(null);
    setIsAdmin(false);
    setIsGuestMode(false);
    console.log('✅ 로그아웃 완료');
  };

  const enterGuestMode = () => {
    setIsGuestMode(true);
    console.log('👤 게스트 모드 진입');
  };

  return (
    <AuthContext.Provider value={{
      user,
      isAdmin,
      isLoading,
      isCheckingAdmin,
      isGuestMode,
      login,
      logout,
      enterGuestMode,
      canSaveAnalysis,
      canLoadAnalysis
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};